var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import React, { useState } from "react";
import { Form } from "radix-ui";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { CheckCircledIcon, CrossCircledIcon } from "@radix-ui/react-icons";
import { RadixInput } from "../primitives/RadixInput";
import { RadixPhoneInput } from "../primitives/RadixPhoneInput";
import { RadixButton } from "../primitives/RadixButton";
import { RadixCheckbox } from "../primitives/RadixCheckbox";
import { useDebouncedValidation } from "../utility/useDebouncedValidation";
import { ValidationMethods } from "../utility/validationMethods";
import { useAuth, useCart, useCheckout, useOrderIntent, useUI } from "../../providers";
import { userSignupSchema } from "./userSignupSchema";
import { SmsMarketingOptIn } from "../smsMarketingOptIn/SmsMarketingOptIn";
import GoogleAnalytics from "../../analytics/google";
import "../ui.css";
import "./userSignup.css";
var shouldAutoCheckRewards = function () {
    var _a, _b;
    if (typeof window === "undefined") {
        return false;
    }
    var session = window.session;
    var sessionValue = (_b = (_a = session === null || session === void 0 ? void 0 : session.store) === null || _a === void 0 ? void 0 : _a.auto_check_rewards) !== null && _b !== void 0 ? _b : session === null || session === void 0 ? void 0 : session.auto_check_rewards;
    if (typeof sessionValue === "boolean") {
        return sessionValue;
    }
    if (typeof sessionValue === "number") {
        return sessionValue === 1;
    }
    if (typeof sessionValue === "string") {
        var normalizedValue = sessionValue.trim().toLowerCase();
        return normalizedValue === "1" || normalizedValue === "true";
    }
    return false;
};
export function UserSignup(_a) {
    var _this = this;
    var _b = _a.embedded, embedded = _b === void 0 ? false : _b, onSwitchToSignIn = _a.onSwitchToSignIn, onSuccess = _a.onSuccess;
    var createUser = useAuth().createUser;
    var setHasMarketingOptIn = useCheckout().setHasMarketingOptIn;
    var getCart = useCart().getCart;
    var _c = useOrderIntent(), currentAddress = _c.currentAddress, setCurrentAddress = _c.setCurrentAddress;
    var _d = useUI(), showToast = _d.showToast, storeUsesRewards = _d.storeUsesRewards;
    var _e = useState(false), isSubmitting = _e[0], setIsSubmitting = _e[1];
    var _f = useState(null), submitError = _f[0], setSubmitError = _f[1];
    var _g = useState(false), isPasswordVisible = _g[0], setIsPasswordVisible = _g[1];
    var _h = useState(false), isConfirmPasswordVisible = _h[0], setIsConfirmPasswordVisible = _h[1];
    var rewardsSignupLabel = "Check this box to start earning reward points";
    var _j = useForm({
        resolver: zodResolver(userSignupSchema),
        mode: "onBlur",
        defaultValues: {
            firstName: "",
            lastName: "",
            email: "",
            confirmEmail: "",
            phone: "",
            password: "",
            confirmPassword: "",
            useRewards: shouldAutoCheckRewards(),
            hasMarketingOptIn: false,
        },
    }), control = _j.control, handleSubmit = _j.handleSubmit, trigger = _j.trigger, watch = _j.watch, _k = _j.formState, errors = _k.errors, isValid = _k.isValid;
    var debouncedTrigger = useDebouncedValidation(trigger);
    var passwordValue = String(watch("password") || "");
    var confirmPasswordValue = String(watch("confirmPassword") || "");
    var passwordRequirements = [
        { label: "At least 8 characters", isMet: passwordValue.length >= 8 },
        { label: "At least 1 uppercase letter", isMet: ValidationMethods.containsUppercaseLetter(passwordValue) },
        { label: "At least 1 number", isMet: ValidationMethods.containsDigit(passwordValue) },
        { label: "At least 1 special character", isMet: ValidationMethods.containsSpecialCharacter(passwordValue) },
        { label: "Passwords match", isMet: Boolean(passwordValue) && passwordValue === confirmPasswordValue },
    ];
    var handleValidSubmit = function (formValues) { return __awaiter(_this, void 0, void 0, function () {
        var createUserResult, createdUser, _error_1;
        var _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    setSubmitError(null);
                    setIsSubmitting(true);
                    _b.label = 1;
                case 1:
                    _b.trys.push([1, 4, 5, 6]);
                    return [4 /*yield*/, createUser({
                            firstName: formValues.firstName.trim(),
                            lastName: formValues.lastName.trim(),
                            email: formValues.email.trim(),
                            phone: formValues.phone.trim(),
                            password: formValues.password,
                            isGuest: false,
                            useRewards: storeUsesRewards && formValues.useRewards,
                            checkoutAddressId: (_a = currentAddress === null || currentAddress === void 0 ? void 0 : currentAddress.id) !== null && _a !== void 0 ? _a : null,
                        })];
                case 2:
                    createUserResult = _b.sent();
                    createdUser = createUserResult.user;
                    if (!createdUser) {
                        setSubmitError(createUserResult.errorMessage || "Unable to create your account. Please verify your details and try again.");
                        return [2 /*return*/];
                    }
                    setHasMarketingOptIn(formValues.hasMarketingOptIn);
                    if (createUserResult.checkoutAddress) {
                        setCurrentAddress(createUserResult.checkoutAddress);
                    }
                    return [4 /*yield*/, getCart()];
                case 3:
                    _b.sent();
                    GoogleAnalytics.userSignUp();
                    showToast("Your account has been created.", "success");
                    onSuccess === null || onSuccess === void 0 ? void 0 : onSuccess();
                    return [3 /*break*/, 6];
                case 4:
                    _error_1 = _b.sent();
                    setSubmitError("Unable to create your account right now. Please try again.");
                    return [3 /*break*/, 6];
                case 5:
                    setIsSubmitting(false);
                    return [7 /*endfinally*/];
                case 6: return [2 /*return*/];
            }
        });
    }); };
    return (React.createElement("section", { className: embedded ? "user-signup user-signup--embedded" : "user-signup", "aria-label": "Create account form" },
        !embedded ? React.createElement("h2", { className: "user-signup-title" }, "Create account") : null,
        React.createElement(Form.Root, { onSubmit: handleSubmit(handleValidSubmit), className: "radix-form user-signup-form", noValidate: true },
            React.createElement("div", { className: "user-signup-form-content" },
                React.createElement("div", { className: "radix-form-field" },
                    React.createElement("label", { className: "user-signup-label", htmlFor: "user-signup-first-name" }, "First name"),
                    React.createElement(Controller, { name: "firstName", control: control, render: function (_a) {
                            var field = _a.field;
                            return (React.createElement(RadixInput, { id: "user-signup-first-name", name: "firstName", autoComplete: "given-name", value: field.value, onChange: function (event) {
                                    field.onChange(event.target.value);
                                    debouncedTrigger("firstName");
                                }, onBlur: field.onBlur, ref: field.ref, "aria-invalid": !!errors.firstName }));
                        } })),
                React.createElement("div", { className: "radix-form-field" },
                    React.createElement("label", { className: "user-signup-label", htmlFor: "user-signup-last-name" }, "Last name"),
                    React.createElement(Controller, { name: "lastName", control: control, render: function (_a) {
                            var field = _a.field;
                            return (React.createElement(RadixInput, { id: "user-signup-last-name", name: "lastName", autoComplete: "family-name", value: field.value, onChange: function (event) {
                                    field.onChange(event.target.value);
                                    debouncedTrigger("lastName");
                                }, onBlur: field.onBlur, ref: field.ref, "aria-invalid": !!errors.lastName }));
                        } })),
                React.createElement("div", { className: "radix-form-field" },
                    React.createElement("label", { className: "user-signup-label", htmlFor: "user-signup-email" }, "Email address"),
                    React.createElement(Controller, { name: "email", control: control, render: function (_a) {
                            var field = _a.field;
                            return (React.createElement(RadixInput, { id: "user-signup-email", name: "email", type: "email", autoComplete: "email", value: field.value, onChange: function (event) {
                                    field.onChange(event.target.value);
                                    debouncedTrigger("email");
                                }, onBlur: field.onBlur, ref: field.ref, "aria-invalid": !!errors.email }));
                        } })),
                React.createElement("div", { className: "radix-form-field" },
                    React.createElement("label", { className: "user-signup-label", htmlFor: "user-signup-confirm-email" }, "Confirm email address"),
                    React.createElement(Controller, { name: "confirmEmail", control: control, render: function (_a) {
                            var field = _a.field;
                            return (React.createElement(RadixInput, { id: "user-signup-confirm-email", name: "confirmEmail", type: "email", autoComplete: "email", value: field.value, onChange: function (event) {
                                    field.onChange(event.target.value);
                                    debouncedTrigger("confirmEmail");
                                }, onBlur: field.onBlur, ref: field.ref, "aria-invalid": !!errors.confirmEmail }));
                        } })),
                React.createElement("div", { className: "radix-form-field" },
                    React.createElement("label", { className: "user-signup-label", htmlFor: "user-signup-phone" }, "Phone number"),
                    React.createElement(Controller, { name: "phone", control: control, render: function (_a) {
                            var field = _a.field;
                            return (React.createElement(RadixPhoneInput, { id: "user-signup-phone", name: "phone", value: field.value, onValueChange: function (value) {
                                    field.onChange(value);
                                    debouncedTrigger("phone");
                                }, onBlur: field.onBlur, ref: field.ref, "aria-invalid": !!errors.phone }));
                        } })),
                React.createElement("div", { className: "radix-form-field" },
                    React.createElement("label", { className: "user-signup-label", htmlFor: "user-signup-password" }, "Password"),
                    React.createElement(Controller, { name: "password", control: control, render: function (_a) {
                            var field = _a.field;
                            return (React.createElement(RadixInput, { id: "user-signup-password", name: "password", type: isPasswordVisible ? "text" : "password", autoComplete: "new-password", value: field.value, onChange: function (event) {
                                    field.onChange(event.target.value);
                                    debouncedTrigger("password");
                                }, onBlur: field.onBlur, ref: field.ref, "aria-invalid": !!errors.password, "aria-describedby": "user-signup-password-help", rightElement: (React.createElement("button", { type: "button", className: "radix-input-icon-button", onClick: function () { return setIsPasswordVisible(function (currentValue) { return !currentValue; }); }, "aria-label": isPasswordVisible ? "Hide password" : "Show password" },
                                    React.createElement("i", { className: "fas ".concat(isPasswordVisible ? "fa-eye-slash" : "fa-eye") }))) }));
                        } }),
                    React.createElement("ul", { id: "user-signup-password-help", className: "user-signup-password-requirements", "aria-live": "polite" }, passwordRequirements.map(function (requirement) { return (React.createElement("li", { key: requirement.label, className: "user-signup-password-requirement ".concat(requirement.isMet ? "user-signup-password-requirement--met" : "user-signup-password-requirement--unmet") },
                        React.createElement("span", { className: "user-signup-password-requirement-icon", "aria-hidden": "true" }, requirement.isMet ? React.createElement(CheckCircledIcon, null) : React.createElement(CrossCircledIcon, null)),
                        React.createElement("span", { className: "user-signup-password-requirement-label" }, requirement.label))); }))),
                React.createElement("div", { className: "radix-form-field" },
                    React.createElement("label", { className: "user-signup-label", htmlFor: "user-signup-confirm-password" }, "Confirm password"),
                    React.createElement(Controller, { name: "confirmPassword", control: control, render: function (_a) {
                            var field = _a.field;
                            return (React.createElement(RadixInput, { id: "user-signup-confirm-password", name: "confirmPassword", type: isConfirmPasswordVisible ? "text" : "password", autoComplete: "new-password", value: field.value, onChange: function (event) {
                                    field.onChange(event.target.value);
                                    debouncedTrigger("confirmPassword");
                                }, onBlur: field.onBlur, ref: field.ref, "aria-invalid": !!errors.confirmPassword, rightElement: (React.createElement("button", { type: "button", className: "radix-input-icon-button", onClick: function () { return setIsConfirmPasswordVisible(function (currentValue) { return !currentValue; }); }, "aria-label": isConfirmPasswordVisible ? "Hide password" : "Show password" },
                                    React.createElement("i", { className: "fas ".concat(isConfirmPasswordVisible ? "fa-eye-slash" : "fa-eye") }))) }));
                        } })),
                storeUsesRewards ? (React.createElement("div", { className: "radix-form-field user-signup-rewards-callout" },
                    React.createElement("p", { className: "user-signup-rewards-callout-title" }, "Rewards signup"),
                    React.createElement(Controller, { name: "useRewards", control: control, render: function (_a) {
                            var field = _a.field;
                            return (React.createElement(RadixCheckbox, { id: "user-signup-use-rewards", checked: Boolean(field.value), onCheckedChange: field.onChange, label: rewardsSignupLabel }));
                        } }),
                    React.createElement("p", { className: "user-signup-rewards-disclaimer" }, "By joining rewards, you agree to receive rewards-related messages."))) : null,
                React.createElement(Controller, { name: "hasMarketingOptIn", control: control, render: function (_a) {
                        var field = _a.field;
                        return (React.createElement(SmsMarketingOptIn, { id: "user-signup-marketing-opt-in", checked: field.value, onCheckedChange: field.onChange, disabled: isSubmitting }));
                    } }),
                submitError ? (React.createElement("span", { className: "radix-error-message", role: "alert" }, submitError)) : null),
            React.createElement("footer", { className: "user-signup-footer" },
                React.createElement(RadixButton, { type: "submit", disabled: !isValid || isSubmitting, className: "radix-submit-button", "aria-busy": isSubmitting }, isSubmitting ? "Creating account..." : "Create account"),
                embedded ? (React.createElement("button", { type: "button", className: "radix-sign-in-forgot-link radix-sign-in-text-button", onClick: onSwitchToSignIn, disabled: isSubmitting }, "Already have an account? Sign in instead.")) : null))));
}
